// frontend/src/lib/evidenceStorage.js
import { supabase } from "./supabaseClient";

export const EVIDENCE_BUCKET = "remediation-evidence";

function asString(v) {
  return String(v ?? "").trim();
}

function safeFilename(name) {
  const raw = asString(name) || "file";
  return raw.replace(/[^a-zA-Z0-9._-]+/g, "_");
}

function safeSegment(v, fallback) {
  const raw = asString(v) || fallback;
  return raw.replace(/[^a-zA-Z0-9._-]+/g, "_");
}

function buildStorageKey({ businessId, snapshotId, findingId, filename }) {
  const bid = safeSegment(businessId, "business_unknown");
  const sid = safeSegment(snapshotId, "snapshot_unknown");
  const fid = safeSegment(findingId, "finding_unknown");
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  const name = safeFilename(filename);
  return `${bid}/${sid}/${fid}/${stamp}_${name}`;
}

function normalizeEvidenceRow(row = {}) {
  return {
    id: row?.id,
    business_id: asString(row?.business_id),
    snapshot_id: asString(row?.snapshot_id),
    finding_id: asString(row?.finding_id),
    filename: row?.filename || "file",
    mime_type: row?.mime_type || "application/octet-stream",
    size_bytes: Number(row?.size_bytes || 0),
    storage_state: "stored",
    storage_key: row?.storage_key || "",
    uploaded_at_iso: row?.uploaded_at || row?.uploaded_at_iso || null,
    created_by: row?.created_by || "",
  };
}

async function requireAuthenticatedUser() {
  const { data, error } = await supabase.auth.getUser();

  if (error) {
    throw new Error(error.message || "Unable to verify signed-in user.");
  }

  const user = data?.user || null;
  if (!user?.id) {
    throw new Error("You must be signed in to upload evidence.");
  }

  return user;
}

export async function uploadEvidenceFile(file, { businessId, snapshotId, findingId } = {}) {
  if (!file) {
    throw new Error("No file selected.");
  }

  const user = await requireAuthenticatedUser();

  const storageKey = buildStorageKey({
    businessId,
    snapshotId,
    findingId,
    filename: file.name,
  });

  const { error: storageError } = await supabase.storage
    .from(EVIDENCE_BUCKET)
    .upload(storageKey, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || undefined,
    });

  if (storageError) {
    throw new Error(storageError.message || "Upload failed.");
  }

  const insertPayload = {
    business_id: asString(businessId),
    snapshot_id: asString(snapshotId),
    finding_id: asString(findingId),
    storage_key: storageKey,
    filename: file.name || "file",
    mime_type: file.type || "application/octet-stream",
    size_bytes: Number(file.size || 0),
    created_by: user.id,
  };

  const { data: dbRow, error: dbError } = await supabase
    .from("remediation_evidence")
    .insert(insertPayload)
    .select()
    .single();

  if (dbError) {
    try {
      await supabase.storage.from(EVIDENCE_BUCKET).remove([storageKey]);
    } catch {
      // ignore cleanup errors
    }
    throw new Error(dbError.message || "Database insert failed.");
  }

  return normalizeEvidenceRow(dbRow || {
    ...insertPayload,
    uploaded_at: new Date().toISOString(),
    id: `evidence_${Date.now()}`,
  });
}

export async function createSignedEvidenceUrl(storageKey, expiresInSeconds = 3600) {
  const key = asString(storageKey);
  if (!key) {
    throw new Error("Missing storage key.");
  }

  const { data, error } = await supabase.storage
    .from(EVIDENCE_BUCKET)
    .createSignedUrl(key, expiresInSeconds);

  if (error) {
    throw new Error(error.message || "Could not create signed URL.");
  }

  const url = data?.signedUrl ? String(data.signedUrl) : "";
  if (!url) {
    throw new Error("Signed URL not returned.");
  }

  return url;
}

export async function removeEvidenceFile(storageKey) {
  const key = asString(storageKey);
  if (!key) return true;

  const { error } = await supabase.storage
    .from(EVIDENCE_BUCKET)
    .remove([key]);

  if (error) {
    throw new Error(error.message || "Could not delete evidence file.");
  }

  return true;
}

export async function listEvidenceFiles({ businessId, snapshotId, findingId } = {}) {
  let query = supabase
    .from("remediation_evidence")
    .select("*");

  const bid = asString(businessId);
  const sid = asString(snapshotId);
  const fid = asString(findingId);

  if (bid) query = query.eq("business_id", bid);
  if (sid) query = query.eq("snapshot_id", sid);
  if (fid) query = query.eq("finding_id", fid);

  const { data, error } = await query.order("uploaded_at", { ascending: false });

  if (error) {
    throw new Error(error.message || "Could not fetch evidence records.");
  }

  return Array.isArray(data) ? data.map(normalizeEvidenceRow) : [];
}

export async function removeEvidenceRecord(evidenceId) {
  const id = asString(evidenceId);
  if (!id) return true;

  const { error } = await supabase
    .from("remediation_evidence")
    .delete()
    .eq("id", id);

  if (error) {
    throw new Error(error.message || "Could not delete evidence record.");
  }

  return true;
}
